<template>
    <Card>
        <template #header>
            <div class="pl-5 pt-4 font-medium text-[16px]">
                Failai
            </div>
        </template>
        <template #content>
            <div class="flex flex-col gap-4">
                <div class="flex flex-row items-start gap-2" v-if="canUploadFiles">
                    <FileUpload
                        ref="fileUploadHandle"
                        mode="basic"
                        customUpload
                        accept=".pdf,.csv,.txt,.doc,.docx,image/*"
                        :maxFileSize="52428800"
                        @uploader="(event: any) => customUploader(event)"
                        :auto="true"
                        chooseIcon="pi pi-upload"
                        :chooseButtonProps="{
                            label: 'Įkelti naują failą',
                            severity: 'contrast',
                            variant: 'outlined',
                            size: 'small'
                        }"
                    />
                </div>

                <div>
                    <div v-if="uploadedFiles.length" class="flex flex-col items-start gap-2">
                        <div v-for="file in uploadedFiles" :key="file.id">
                            <div
                                class="p-1 border border-gray-300 rounded flex flex-row items-center gap-2 relative cursor-pointer text-[13px]"
                                @click.prevent="openFile(file)"
                            >
                                <div class="flex flex-row justify-center items-center gap-2 min-w-0">
                                    <PVButton
                                        v-if="canManagePartnerVisibility"
                                        type="button"
                                        icon="pi pi-users"
                                        severity="info"
                                        outlined
                                        size="small"
                                        class="!w-7 !h-7 !rounded-md !border !border-sky-500 !text-sky-600 hover:!bg-sky-50"
                                        title="Failo matomumas partneriams"
                                        @click.stop.prevent="openVisibilityPopover($event, file.id)"
                                    />

                                    <i class="pi pi-file !text-[14px]"></i>

                                    <span class="truncate">{{ file.name }}</span>
                                </div>

                                <div
                                    v-if="canDeleteFiles"
                                    class="flex bg-red-500 hover:bg-red-600 text-white p-[8px] rounded items-center justify-center cursor-pointer"
                                    @click.stop.prevent="deleteFile(file.id)"
                                >
                                    <i class="pi pi-trash !text-[0.8rem]"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </Card>

    <Popover ref="visibilityPopover" v-if="canManagePartnerVisibility">
        <div class="flex flex-col gap-2 w-[340px]">
            <div class="text-sm font-medium">Failo matomumas partneriams</div>
            <MultiSelect
                v-model="activeFileVisibilitySelection"
                :options="partnerUsers"
                optionLabel="name"
                optionValue="code"
                display="chip"
                placeholder="Pasirinkite partnerius"
                class="w-full"
                size="small"
                @change="updateActiveFileVisibility"
            />
        </div>
    </Popover>
</template>

<script setup lang="ts">
// System imports
import { ref, onMounted, computed } from 'vue'
import { apiRequest } from '@/services/axios.ts'
import { is } from '@/extensions/permissions.ts'
import { openProtectedFile } from '@/services/fileDownload.ts'

// PrimeVue components imports
import { useConfirm } from 'primevue/useconfirm'
import FileUpload from 'primevue/fileupload'
import Card from 'primevue/card'
import MultiSelect from 'primevue/multiselect'
import Popover from 'primevue/popover'
import PVButton from 'primevue/button'

// Refs and variables
const confirm = useConfirm()
const fileUploadHandle = ref()
const uploadedFiles = ref<any[]>([])
const partnerUsers = ref<Array<{ code: number; name: string }>>([])
const fileVisibilitySelection = ref<Record<number, number[]>>({})
const canManagePartnerVisibility = ref<boolean>(false)
const canUploadFiles = ref<boolean>(false)
const canDeleteFiles = ref<boolean>(false)
const visibilityPopover = ref()
const activeVisibilityFileId = ref<number | null>(null)

type UploadedFile = {
    id: number
    name: string
}

// Props
let props = defineProps<{
    contact: any
}>()

const activeFileVisibilitySelection = computed<number[]>({
    get() {
        if (activeVisibilityFileId.value === null) {
            return []
        }

        return fileVisibilitySelection.value[activeVisibilityFileId.value] ?? []
    },
    set(value: number[]) {
        if (activeVisibilityFileId.value === null) {
            return
        }

        fileVisibilitySelection.value[activeVisibilityFileId.value] = Array.isArray(value)
            ? value
            : []
    }
})

// Hooks
onMounted(() => {
    loadUploadedFiles()
})

// Methods
async function loadUploadedFiles() {
    const response = await apiRequest('get', '/contacts/' + props.contact.id + '/files')

    const data = response.data.data ?? {}
    uploadedFiles.value = data.files ?? []
    partnerUsers.value = data.partner_users ?? []
    canManagePartnerVisibility.value = Boolean(data.can_manage_partner_visibility)
    canUploadFiles.value = Boolean(data.can_upload_files ?? is('manager|admin|partner_collab'))
    canDeleteFiles.value = Boolean(data.can_delete_files ?? is('manager|admin'))

    const visibilityByFile: Record<number, number[]> = {}
    uploadedFiles.value.forEach((file: any) => {
        visibilityByFile[file.id] = Array.isArray(file.visible_partner_user_ids)
            ? file.visible_partner_user_ids
            : []
    })
    fileVisibilitySelection.value = visibilityByFile
}

async function updateFileVisibility(fileId: number) {
    await apiRequest('patch', '/contacts/' + props.contact.id + '/files/' + fileId + '/visibility', {
        partner_user_ids: fileVisibilitySelection.value[fileId] ?? []
    })
}

function openVisibilityPopover(event: Event, fileId: number) {
    activeVisibilityFileId.value = fileId
    visibilityPopover.value?.toggle(event)
}

async function updateActiveFileVisibility() {
    if (activeVisibilityFileId.value === null) {
        return
    }

    await updateFileVisibility(activeVisibilityFileId.value)
}

async function deleteFile(fileId: number) {
    confirm.require({
        message: 'Ar tikrai norite ištrinti šį failą?',
        header: 'Patvirtinimas',
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
            label: 'Ne',
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: 'Taip'
        },
        accept: async () => {
            await apiRequest('delete', '/contacts/' + props.contact.id + '/files/' + fileId + '/delete')

            await loadUploadedFiles()
        }
    })
}

const customUploader = async ({ files }: { files: File[] }) => {
    const formData = new FormData()

    files.forEach((file: File) => {
        formData.append('docs[]', file)
    })

    await apiRequest('post', '/contacts/' + props.contact.id + '/upload', formData)

    await loadUploadedFiles()
}

async function openFile(file: UploadedFile) {
    await openProtectedFile('/contacts/' + props.contact.id + '/files/' + file.id + '/download', file.name)
}
</script>
